import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { userHasRight } from "selectors/rights";
import { EMPLOYEE_COMPLAINT_EDIT } from "typeConstants/rights";
import { getEmployeeComplaints } from "requests/employeeComplaints";
import { EmployeeComplaintResponse, EmployeeDetailDto } from "interfaces/Mueller.Business.Models.ts";
import { MCard, MGrid, MSpinner } from "components/Shared";
import { defaultHeightStyle } from "styles/sharedStyles";
import { COL_CODE_CITY, COL_CODE_CONTROL_NUMBER, COL_CODE_CUSTOMER_NAME, COL_CODE_CUSTOMER_NUMBER, COL_CODE_DATE, COL_CODE_FULL_NAME, FORMATTER_LEFT_ALIGN } from "components/Shared/MGrid.tsx";
import { DATA_TYPE_BUTTON } from "typeConstants/dataTypes";
import EmployeeComplaintAddDialog from "./EmployeeComplaintAddDialog.tsx";
import EmployeeComplaintViewDialog from "./EmployeeComplaintViewDialog.tsx";
import { getEmployeeDetailsByEmployeeId } from "requests/employee/index";
import responseHandler from "responseHandler/index";

interface Props {
  employeeId: number,
  cellWidth?: number,
  cardTitleOverride?: string,
  viewOnly?: boolean,
  //redux
  canEditComplaintCard: boolean,
  makeToast: (text: string, type: string) => void,
}

const EmployeeComplaintCard = (props: Props) => {

  const mounted = useRef<boolean>(true);

  const [isFetchingComplaints, setIsFetchingComplaints] = useState<boolean>(true);
  const [showAddComplaintDialog, setShowAddComplaintDialog] = useState<boolean>(false);
  const [showSelectedComplaintDialog, setShowSelectedComplaintDialog] = useState<boolean>(false);
  const [employeeComplaints, setEmployeeComplaints] = useState<EmployeeComplaintResponse[]>(null);
  const [selectedComplaint, setSelectedComplaint] = useState<EmployeeComplaintResponse>(null);
  const [isFetchingEmployee, setIsFetchingEmployee] = useState<boolean>(true);
  const [employeeName, setEmployeeName] = useState<string>("");

  useEffect(() => {
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {

    getEmployeeComplaints(
      props.employeeId,
      (complaints: EmployeeComplaintResponse[]) => {
        setEmployeeComplaints(complaints);
        setIsFetchingComplaints(false);
      },
      () => {props.makeToast("Failed to fetch feedback for employee", "error");},
      mounted
    );

    getEmployeeDetailsByEmployeeId(props.employeeId, (e: EmployeeDetailDto) => {
      setEmployeeName(e.name);
      setIsFetchingEmployee(false);
    }, mounted);

  }, [props.employeeId]);

  const VIEW_COLUMN_KEY = "VIEW_COLUMN_KEY";

  const onCellButtonClick = (selectedOriginalIndex: number, columnKey: string) => {
    if (columnKey === VIEW_COLUMN_KEY) {
      setSelectedComplaint(employeeComplaints[selectedOriginalIndex]);
      setShowSelectedComplaintDialog(true);
    }
  };

  const onEmployeeComplaintViewDialogClose = () => {
    setShowSelectedComplaintDialog(false);
    setSelectedComplaint(null);
  };

  const viewDetailsButton = () => <i className="md-icon material-icons">visibility</i>;

  const onComplaintSubmit = () => {
    setShowAddComplaintDialog(false);
    getEmployeeComplaints(
      props.employeeId
      ,(complaints: EmployeeComplaintResponse[]) => {
        setEmployeeComplaints(complaints);
        setIsFetchingComplaints(false);
        props.makeToast("Employee feedback added", "success");
      }
      , () => {
        props.makeToast("Failed to retrieve feedback after submitting new feedback. Try reloading the profile", "error");
      }
      , mounted);
  };

  if (isFetchingComplaints || isFetchingEmployee)
    return <MSpinner />;
  return (
    <>
      <MCard
        title={props.cardTitleOverride ?? "Employee FR Feedback"}
        cellWidth={props.cellWidth}
        actions={props.canEditComplaintCard && !props.viewOnly ? [{ icon: "add", onClick: () => setShowAddComplaintDialog(true) }] : null}
        children={
          <MGrid
            data={employeeComplaints}
            disableFilters={true}
            wrapperStyle={defaultHeightStyle}
            columnDefinitions={[
              { code: COL_CODE_DATE, dataSource: "dateCreated", name: "Date Created", width: 150 },
              { code: COL_CODE_FULL_NAME, dataSource: "enteredByUser", name: "Entered By", width: 200 },
              { code: COL_CODE_CITY , dataSource: "categoryName", name: "Category", width: 150 }, //If I don't provide a code no value is displayed in the grid. Using whatever just to get it to work
              { code: COL_CODE_CUSTOMER_NUMBER, dataSource: "surveyScore", name: "Score", width: 60 },
              { code: COL_CODE_CONTROL_NUMBER, dataSource: "controlIdNumber", name: "Control#", width: 100, formatter: FORMATTER_LEFT_ALIGN },
              { code: COL_CODE_CUSTOMER_NAME, dataSource: "customerNameNumber", name: "Customer", width: 200 }, //Same as category column
              { key: VIEW_COLUMN_KEY, dataSource: viewDetailsButton, dataType: DATA_TYPE_BUTTON, tooltip: "View Complaint Details", name: "View", width: 80, formatter: FORMATTER_LEFT_ALIGN }
            ]}
            onCellButtonClick={onCellButtonClick}
          />
        }
      />
      <EmployeeComplaintAddDialog visible={showAddComplaintDialog} employeeId={props.employeeId} onCancel={() => setShowAddComplaintDialog(false)} onSubmit={onComplaintSubmit} employeeName={employeeName} />
      {selectedComplaint ? <EmployeeComplaintViewDialog visible={showSelectedComplaintDialog} complaint={selectedComplaint} onClose={onEmployeeComplaintViewDialogClose} /> : <></>}

    </>
  );
};

const mapStateToProps = (state) => {
  return {
    canEditComplaintCard: userHasRight(EMPLOYEE_COMPLAINT_EDIT)(state),
  };
};

const mapDispatchToProps = dispatch => ({
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeComplaintCard);